import { forwardRef, type ReactNode } from 'react';

import { theme } from '@prose-ui';
import { legacyTheme, styled } from '@prose-ui/legacy';
import { makeStyles } from 'legacyStyles';

import type { StaticImageData } from 'Components/Image';
import { TextImage } from 'Components/TextImage';
import { breakpoints } from 'Components/ThemeProvider';
import { Typography } from 'Components/Typography';

import LandingPageCTA from 'Containers/LandingPageCTA';

import useMedia from 'utils/useMedia';

const IngredientGrid = styled.div`
  display: flex;
  flex: 0 1 100px;
  flex-wrap: wrap;
  width: 100%;
`;

const IngredientCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 200px;
  min-width: 100px;
  margin: ${legacyTheme.spacing.s16} auto auto auto;
`;

const IngredientImage = styled.img`
  width: auto;
  height: 135px;
`;

const IngredientName = styled(Typography)`
  flex-grow: 1;
`;

const useStyles = makeStyles({
  rootClass: {
    background: theme.colors.neutral[200],
    '& h3': {
      fontFamily: legacyTheme.typography.simplonNorm,
      textTransform: 'uppercase',
    },
    '& p': {
      color: theme.colors.primary[400],
    },
    '& a': {
      marginBottom: 40,
    },
  },
});

type FormulaProps = {
  title: string;
  description: string;
  heroImagesAlt?: string;
  formulaImages: Array<{
    src: StaticImageData;
  }>;
  naturalIngredients: Array<{
    name: string;
    image: StaticImageData;
    benefit: string;
  }>;
  dataTestId?: string;
  id?: string;
  hasUncroppedMobileImage?: boolean;
  children?: ReactNode;
};

export const Formula = forwardRef<HTMLElement, FormulaProps>(
  (
    {
      title,
      description,
      naturalIngredients,
      dataTestId,
      id,
      formulaImages,
      heroImagesAlt = '',
      hasUncroppedMobileImage = false,
      children = null,
    },
    ref,
  ) => {
    const isMobile = useMedia(`(max-width: ${breakpoints.sm - 1}px)`);
    const { classes } = useStyles();

    const forwardedRef = ref as React.RefObject<HTMLDivElement>;
    const [naturalWondersMobile, naturalWondersTablet, naturalWondersDesktop] = formulaImages.map(
      (image) => image.src,
    );

    return (
      naturalWondersDesktop &&
      naturalWondersMobile && (
        <TextImage
          ref={forwardedRef}
          classes={{ root: classes.rootClass }}
          contentAlign="center"
          data-testid={dataTestId}
          description={description}
          descriptionProps={{ variant: 'p2' }}
          hasUncroppedMobileImage={hasUncroppedMobileImage}
          id={id}
          imageAlt={heroImagesAlt}
          imageDesktop={naturalWondersDesktop}
          imageMobile={naturalWondersMobile}
          imageTablet={naturalWondersTablet}
          title={title}
          titleProps={{ variant: 'h1r', paragraph: true }}
          wideScreenOrder="21"
        >
          <>
            {!isMobile &&
              (children ?? <LandingPageCTA productLabel="formula" theme="underlinedBlack" />)}

            <Typography color="grey" paragraph variant="mono2">
              Featuring high-performing naturals like:
            </Typography>

            <IngredientGrid>
              {naturalIngredients.map(({ image, name, benefit }) => (
                <IngredientCard key={name}>
                  <IngredientImage alt={name} height="135" src={image.src} />

                  <IngredientName color="grey" variant="p3">
                    {name}
                  </IngredientName>

                  <Typography color="grey" variant="p4">
                    {benefit}
                  </Typography>
                </IngredientCard>
              ))}
            </IngredientGrid>
          </>
        </TextImage>
      )
    );
  },
);

Formula.displayName = 'Formula';
